import { useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { IconButton, AppBreadcrumb } from '@/components/common';
import { cn } from '@/lib/utils/cn';
import { useMobileSidebar } from './use-mobile-sidebar';
import { AppHeader } from './app-header';
import { NotificationBell } from '@/features/notifications/components';
import { useQuote } from '@/services/queries/quotes';

interface ClientHeaderProps {
  className?: string;
}

export function getClientQuoteIdFromPathname(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'client' || segments[1] !== 'quotes' || !segments[2] || segments[2] === 'new') {
    return null;
  }
  return segments[2];
}

export function buildClientBreadcrumbs(pathname: string, quoteReferenceNumber?: string): Array<{ label: string; href?: string; current?: boolean }> {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: Array<{ label: string; href?: string; current?: boolean }> = [];

  if (segments[0] === 'client') {
    breadcrumbs.push({ label: 'Dashboard', href: '/client' });

    if (!segments[1] || segments[1] === 'dashboard') {
      breadcrumbs[breadcrumbs.length - 1].current = true;
    } else if (segments[1] === 'quotes') {
      breadcrumbs.push({ label: 'Quotes', href: '/client/quotes', current: !segments[2] });
      if (segments[2] === 'new') {
        breadcrumbs.push({ label: 'New Quote', current: true });
      } else if (segments[2]) {
        breadcrumbs.push({ label: quoteReferenceNumber || 'SVQ-xxx', current: true });
      }
    } else if (segments[1] === 'address-book') {
      breadcrumbs.push({ label: 'Address Book', href: '/client/address-book', current: !segments[2] });
      if (segments[2] === 'new') {
        breadcrumbs.push({ label: 'New Address', current: true });
      }
    }
  }

  return breadcrumbs;
}

export function ClientHeader({ className }: ClientHeaderProps) {
  const router = useRouterState();
  const { toggleMobileSidebar } = useMobileSidebar();
  const [showHelp, setShowHelp] = useState(false);
  const pathname = router.location.pathname;
  const quoteId = getClientQuoteIdFromPathname(pathname);
  const { data: quote } = useQuote(quoteId ?? '', !!quoteId);

  const breadcrumbs = buildClientBreadcrumbs(pathname, quote?.referenceNumber);

  return (
    <AppHeader
      className={cn(
        'gap-2 sm:gap-4 border-gray-200 bg-white px-3 sm:px-6 dark:border-gray-800 dark:bg-gray-900',
        className,
      )}
    >
      {/* Mobile menu button */}
      <IconButton
        icon={<MENU className="h-5 w-5" />}
        className="lg:hidden shrink-0"
        onClick={toggleMobileSidebar}
        aria-label="Toggle menu"
      />

      {/* Breadcrumbs */}
      <AppBreadcrumb items={breadcrumbs} className="min-w-0 flex-1" />

      {/* Actions */}
      <div className="flex items-center gap-1 sm:gap-3 shrink-0">
        {/* Help Button with Popover */}
        <div className="relative">
          <IconButton
            icon={<HELP className="h-5 w-5" />}
            onClick={() => setShowHelp(!showHelp)}
            aria-label="Help"
          />
          {showHelp && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowHelp(false)}
              />
              <div className="absolute right-0 top-full mt-2 z-50 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-3 pb-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                    <HEADPHONES className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Need Help?</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">We're here for you</p>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  <a
                    href="tel:+15551234567"
                    className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50"
                  >
                    <PHONE className="h-4 w-4 text-gray-400" />
                    <span>Call Us: +1 555-123-4567</span>
                  </a>
                  <a
                    href="mailto:support@snapes.com"
                    className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50"
                  >
                    <MAIL className="h-4 w-4 text-gray-400" />
                    <span>Email Support</span>
                  </a>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Notifications */}
        <NotificationBell />
      </div>
    </AppHeader>
  );
}

// Icon components
function MENU({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

function HELP({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function HEADPHONES({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function PHONE({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  );
}

function MAIL({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}
